import DataSourceWidgetModel from './data-source-widget-model'
import resolveWidgetPeriod, {extendCalendarError} from './resolve-widget-period'
import config from '../../components/config'
import _ from 'lodash'
import {DATASETS, INITIATIVES, SCORECARDS} from '../../components/sections'
import calendars from '../../components/calendars'
import getAggregatedDataType from '../../components/get-aggregated-data-type'
import { getPrecisionOrDefault } from '../../components/format-value'
import {
	dataTypeForInitCalcType
} from '../../initiatives/format-initiative-calculations'
import { CURRENCY, NUMBER } from '../../datasets/model/data-type'
import { DATA_SOURCE_PROPERTY } from './shape/shape-widget-properties'
import { USE_EQUATION } from '../../datasets/model/aggregation-type'
import WidgetModel from './widget-model'

/**
 * Common functionality for speedometers and linear gauges
 */
const BaseGaugeModel = DataSourceWidgetModel.extend({

	isInteractive: function () {
		return false
	},

	/**
	 * Returns the options needed to render the gauge. This should be
	 * implemented by subclasses
	 */
	gaugeOptions: function({ datasetQueryResponse } = {}) {
		let props = this.properties.toJSON()
		return {
			label: props.showLabel,
			labelText: props.label,
			organization: props.showOrganization,
			period: props.showPeriod,
			score: props.showScore,
			[DATA_SOURCE_PROPERTY]: props[DATA_SOURCE_PROPERTY],
			datasetQueryResponse: datasetQueryResponse
				?? this.get('datasetQueryResponse')
		}
	},

	/**
	 * Returns "relevant data" for the gauge component. Depending on your use
	 * case, you may be better off calling gaugeOptions, which has more data
	 * available
	 */
	relevantData: function () {
		let periodId = resolveWidgetPeriod(this)
		var props = this.properties.toJSON()
		let error = periodId ? null : extendCalendarError(props.calendarId)

		var id = props.domainObjectId + ''
		let section = props.section ?? SCORECARDS
		var domainObj = this.data[section](id) || {}
		if (section === DATASETS) {
			// deep clones of datasets with lots of linked datasets take too
			// long
			domainObj = _.clone(domainObj)
		} else {
			domainObj = _.cloneDeep(domainObj)
		}

		var relevant = {
			domainObj,
			// for backwards compatibility
			scorecardNode: domainObj,
			// #29441 always pass along the periodId so widgets with no score
			// can display their period
			periodId
		}

		if (this.section() === DATASETS) {
			let dataset = this.data.datasetModel(id)
			let fieldId = this.properties.get('fieldId')
			let field = dataset.combinedFieldsById[fieldId]
				// #58828 since the pseudo-field "Number of Records" is not
				// found in combinedFieldsById, we can use looking it up by
				// fieldId as a fallback
				|| dataset.getFieldByFieldIdentifier(fieldId)
			relevant.dataType = this.getDatasetsDataType(field)
			relevant.currencyCode = field?.currencyCode
			relevant.precision = getPrecisionOrDefault(field?.precision,
				relevant.dataType)
		} else if (this.section() === INITIATIVES) {
			let dataSourceProperty = this.properties.get('dataSourceProperty')
			let dataType = NUMBER
			if (dataSourceProperty) {
				dataType = dataTypeForInitCalcType(dataSourceProperty)
			}
			relevant.dataType = dataType
			relevant.currencyCode = domainObj.currencyCode
			relevant.precision = dataType === CURRENCY ?
				config.defaultCurrencyPrecision : config.defaultPrecision
		}

		var score = domainObj.scores && domainObj.scores[periodId]

		relevant.currentScore = score

		var previousPeriod = calendars.relativePeriod(periodId, -1)
		if (previousPeriod) {
			relevant.previousScore = domainObj.scores && domainObj.scores[previousPeriod.id]
		}

		relevant.currentCalculations = domainObj.calculations?.[periodId]
		// there was some weird stuff going on with order of operations when calling
		// model.get('wob'), so bypass Backbone and just pull the info directly
		relevant.thresholdColoring = this.wob.attributes.thresholdColoring

		var org = this.data.organizations(domainObj.organizationId)
		if (org)  {
			relevant.scorecardNode.organization = org.label
		}

		relevant.hoverText = this.hoverText({
			domainObjectId: id,
			displayedText: domainObj.label
		})

		relevant.error = error

		return relevant
	},

	/**
	 * Returns the correct data type for the gauge based on the dataset field
	 * and this.properties. For dataset gauges only.
	 */
	getDatasetsDataType: function(field) {
		if (USE_EQUATION == this.properties.get('aggregationType')) {
			return this.properties.get('dataType')
		} else {
			return getAggregatedDataType(field?.dataType,
				this.properties.get('aggregationType'))
		}
	},

	/**
	 * @override
	 */
	validShowPropertyKeys () {
		return this.validShowProperties().map(p => p.property)
	},

	/**
	 * @override
	 */
	validShowProperties: function () {
		var props = [
			{ property: 'showLabel', label: 'common.name' }
		]

		if (this.section() !== 'datasets') {
			props.push({ property: 'showOrganization', label: 'common.organization.organization' })
		}

		if (['scorecards', 'initiatives'].includes(this.section())) {
			props.push({ property: 'showPeriod', label: 'common.calendar.period'})
		}

		if (this.section() === 'scorecards') {
			if (config.scoresVisible) {
				props.push({ property: 'showScore', label: 'scorecards.node.score' })
			}
			if (this.type === 'speedometer') {
				props.push({ property: 'showChange', label: 'dashboards.speedometer.changeFromPrevious' })
			} else {
				props.push({ property: 'showPrevious', label: 'dashboards.linear.previousScore' })
			}

		}

		return props
	},

	/**
	 * @override
	 */
	drilldown(drilldown) {
		if (drilldown) {
			// When setting the drilldown, the standard widget logic is fine
			return WidgetModel.prototype.drilldown.apply(this, arguments)
		}

		if (!this.isDatasetSource()
			|| this.properties.get('drilldownSection') !== 'default') {

			// The default widget drilldown logic is fine for anything other
			// than the "default" setting with dataset sources
			return WidgetModel.prototype.drilldown.apply(this, arguments)
		}

		// #28088 don't allow default drilldown for calculated widgets
		if (this.properties.get('dataSourceType') === 'calculated') {
			return null
		}

		return {
			section: 'datasets',
			id: this.datasetId(),
			request: this.datasetRequest()
		}
	}
})

export default BaseGaugeModel

BaseGaugeModel.__filename__ = 'dashboards/widgets/base-gauge-model.js'
